import type { LandingPageContent } from '../types'

// GitLab-Issue #143: polnische Übersetzung, gleiche topicId wie die deutsche/spanische Fassung.
// Screenshots wie bei der ES-Version dieselben Bilddateien aus der deutschsprachigen Test-Session -
// im Intro transparent gemacht.
export const JAK_DZIALA_DLA_KLIENTOW: LandingPageContent = {
  slug: 'jak-dziala-dla-klientow',
  lang: 'pl',
  topicId: 'so-funktioniert-es-fuer-kunden',
  title: 'Jak działa Roofora dla klientów – instrukcja krok po kroku',
  metaDescription:
    'Instrukcja krok po kroku dla klientów: założenie konta, publikacja zapytania ze zdjęciami ' +
    'dachu i skrzynki licznikowej, porównanie ofert i udostępnienie kontaktu tylko wybranej firmie.',
  keywords: [
    'Roofora instrukcja',
    'Roofora jak to działa',
    'jak złożyć zapytanie fotowoltaiczne',
    'jak porównać oferty fotowoltaiczne',
    'udostępnienie kontaktu instalatorowi',
  ],
  eyebrow: 'Instrukcja dla klientów',
  h1: 'Jak działa Roofora dla klientów',
  intro:
    'Ta instrukcja pokazuje każdy krok z perspektywy klienta - od pierwszej rejestracji po ' +
    'udostępnienie kontaktu wybranej firmie instalatorskiej - z prawdziwymi zrzutami ekranu z ' +
    'aplikacji. Zrzuty pochodzą z sesji testowej w języku niemieckim; aplikacja dostępna jest też po ' +
    'polsku, więc przyciski w Twojej sesji pojawią się w Twoim języku.',
  sections: [
    { type: 'heading', text: 'Wyjaśnione w 3 minuty' },
    {
      type: 'paragraph',
      text:
        'Zakładasz anonimowe konto, dodajesz zdjęcia dachu i skrzynki licznikowej i na tej podstawie ' +
        'publikujesz zanonimizowane zapytanie. Firmy instalatorskie z Twojego regionu składają ' +
        'niezależnie od siebie ofertę. Porównujesz ceny i udostępniasz swoje dane kontaktowe dopiero, ' +
        'gdy zdecydujesz się na konkretną firmę.',
    },
    { type: 'heading', text: 'Co jest potrzebne wcześniej?' },
    {
      type: 'list',
      items: [
        'Od jednego do trzech zdjęć dachu lub domu (wystarczą zdjęcia z telefonu)',
        'Zdjęcie skrzynki licznikowej',
        'Kod pocztowy',
        'Przybliżone dane o budynku, dachu i zużyciu prądu - wiele pól ma już sensowne wartości domyślne',
        'Nie jest potrzebny e-mail ani numer telefonu, wystarczy nazwa użytkownika i hasło',
      ],
    },
  ],
  steps: [
    {
      title: 'Start na stronie głównej',
      whatYouSee:
        'W górnej sekcji roofora.de widoczne są dwa duże przyciski - jeden dla klientów, drugi dla ' +
        'firm instalatorskich - a poniżej lista argumentów zaufania, np. „konto bez e-maila i telefonu”.',
      whatToDo: 'Kliknij główny przycisk przeznaczony dla klientów.',
      whatHappensNext: 'Zostaniesz przeniesiony na stronę rejestracji dla klientów z zakładkami „Zarejestruj się” i „Zaloguj się”.',
      successCriterion: 'Widoczny jest ekran zakładania konta z polami nazwy użytkownika i hasła.',
      screenshot: {
        src: '/how-to/kunde/01-hero-cta.jpg',
        alt: 'Strona główna Roofora z przyciskami dla klientów i firm instalatorskich',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Rejestracja anonimowego konta',
      whatYouSee:
        'Formularz z polami nazwy użytkownika (z linkiem do wygenerowania losowej nazwy) i hasła, a ' +
        'także informacja, że po rejestracji otrzymasz kod odzyskiwania.',
      whatToDo:
        'Wygeneruj nazwę użytkownika lub wpisz własną, ustaw hasło o długości co najmniej 8 znaków i ' +
        'potwierdź założenie konta.',
      whatHappensNext: 'Dostępność nazwy użytkownika jest sprawdzana na bieżąco.',
      successCriterion: 'Oba pola są wypełnione, przycisk zakładania konta jest aktywny.',
      troubleshooting: 'Jeśli nazwa użytkownika jest zajęta, od razu pojawia się informacja - wystarczy wybrać inną.',
      screenshot: {
        src: '/how-to/kunde/02-registrierung.jpg',
        alt: 'Wypełniony formularz rejestracji z nazwą użytkownika i hasłem, informacja o dostępności',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Zapisanie kodu odzyskiwania',
      whatYouSee:
        'Nazwa użytkownika, hasło (ukryte) i kod odzyskiwania, z opcjami skopiowania lub pobrania jako plik tekstowy.',
      whatToDo:
        'Zapisz przynajmniej kod odzyskiwania w bezpiecznym miejscu, np. pobierając go, i zaznacz ' +
        'pole potwierdzające zapisanie hasła i kodu.',
      whatHappensNext: 'Przycisk przejścia do platformy aktywuje się dopiero po zaznaczeniu pola.',
      successCriterion: 'Trafiasz do panelu klienta.',
      troubleshooting:
        'Nie ma resetu hasła przez e-mail - bez kodu odzyskiwania konto nie da się przywrócić po ' +
        'utracie hasła.',
      screenshot: {
        src: '/how-to/kunde/03-account-bereit.jpg',
        alt: 'Ekran potwierdzenia z nazwą użytkownika, hasłem i kodem odzyskiwania',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Panel klienta',
      whatYouSee:
        'Nazwa użytkownika, identyfikator klienta oraz trzy kafelki - „Mój profil”, „Moje zapytania” i ' +
        '„Moje udostępnienia kontaktu” - a także przycisk utworzenia nowego zapytania.',
      whatToDo: 'Kliknij przycisk utworzenia nowego zapytania, aby zacząć pierwsze zapytanie solarne.',
      whatHappensNext: 'Otwiera się 6-etapowy kreator „Utwórz zlecenie”.',
      successCriterion: 'Widoczne są zakładki od kroku 1 do kroku 6.',
      screenshot: {
        src: '/how-to/kunde/04-dashboard.jpg',
        alt: 'Panel klienta z kafelkami Mój profil, Moje zapytania, Moje udostępnienia kontaktu',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Kreator: adres i budynek',
      whatYouSee:
        'Podgląd na żywo z wstępną rekomendacją instalacji i opłacalnością, a poniżej pola dotyczące ' +
        'typu zlecenia, kraju, kodu pocztowego, typu budynku, roku budowy, kształtu i materiału dachu.',
      whatToDo:
        'Wypełnij pola - wiele z nich ma już sensowne wartości domyślne. Obowiązkowy jest tylko kod pocztowy.',
      whatHappensNext: 'Podgląd aktualizuje się automatycznie po każdej zmianie.',
      successCriterion: 'Wszystkie obowiązkowe pola są wypełnione, przycisk „Dalej” prowadzi do kroku 2.',
      troubleshooting: 'Bez kodu pocztowego nie da się przejść dalej - jest to wyraźnie zaznaczone w formularzu.',
      screenshot: {
        src: '/how-to/kunde/05-wizard-start.jpg',
        alt: 'Krok kreatora „Adres i budynek” z podglądem na żywo i wyborem typu zlecenia',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Dodanie zdjęć dachu',
      whatYouSee:
        'Dla każdej połaci dachu pola dotyczące orientacji, powierzchni i nachylenia, a poniżej pole ' +
        'do przesyłania zdjęć dachu/domu z licznikiem „0/3 zdjęcia”.',
      whatToDo: 'Prześlij od jednego do trzech zdjęć dachu lub domu - wystarczą zdjęcia z telefonu.',
      whatHappensNext: 'Przesłane zdjęcia pojawiają się jako miniatury z opcją usunięcia, a licznik zmienia się na „1/3 zdjęcia”.',
      successCriterion: 'Przesłano co najmniej jedno zdjęcie dachu.',
      troubleshooting: 'Bez co najmniej jednego zdjęcia zapytania nie da się później opublikować.',
      screenshot: {
        src: '/how-to/kunde/06-dachfotos.jpg',
        alt: 'Pole przesyłania zdjęć dachu z jednym przesłanym zdjęciem, licznik 1/3 zdjęcia',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Dodanie zdjęcia skrzynki licznikowej',
      whatYouSee:
        'W kroku 3 kreatora pole do przesyłania zdjęć skrzynki licznikowej oraz pytania dotyczące ' +
        'okablowania i miejsca montażu falownika/magazynu energii.',
      whatToDo:
        'Prześlij co najmniej jedno zdjęcie skrzynki licznikowej i odpowiedz na pytania techniczne wedle ' +
        'swojej wiedzy - „nie wiem” jest zawsze poprawną odpowiedzią.',
      whatHappensNext: 'Licznik zdjęć zmienia się na „1/2 zdjęcia”.',
      successCriterion: 'Przesłano co najmniej jedno zdjęcie skrzynki licznikowej.',
      troubleshooting: 'To zdjęcie jest również obowiązkowe, bez niego nie da się kontynuować.',
      screenshot: {
        src: '/how-to/kunde/07-zaehlerschrank-foto.jpg',
        alt: 'Pole przesyłania zdjęć skrzynki licznikowej z jednym przesłanym zdjęciem',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Ustawienie preferencji kontaktu i przedziału czasowego',
      whatYouSee:
        'W ostatnim kroku kreatora opcja zgody na kontakt oraz kilka przedziałów czasowych do wyboru.',
      whatToDo:
        'Wybierz „tak”, jeśli firmy instalatorskie mają móc się z Tobą kontaktować po Twojej ' +
        'indywidualnej zgodzie, i zaznacz preferowany przedział czasowy.',
      whatHappensNext: 'Te dane trafiają wyłącznie do firm, które sam później zaakceptujesz - nie automatycznie do wszystkich.',
      successCriterion: 'Wybrany jest jeden przedział czasowy.',
      screenshot: {
        src: '/how-to/kunde/08-kontakt-zeitfenster.jpg',
        alt: 'Wybór zgody na kontakt i preferowanego przedziału czasowego w kreatorze',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Publikacja zapytania',
      whatYouSee:
        'Ostateczne podsumowanie z rekomendacją instalacji, opłacalnością i symulacją finansowania, a ' +
        'poniżej przyciski „Zapisz szkic” i „Opublikuj”.',
      whatToDo: 'Sprawdź podsumowanie i potwierdź publikację.',
      whatHappensNext:
        'Twoje zapytanie staje się widoczne w formie zanonimizowanej dla firm instalatorskich z ' +
        'Twojego regionu - bez Twoich danych kontaktowych.',
      successCriterion: 'Pojawia się zielony status „Opublikowano” oraz termin składania ofert.',
      troubleshooting: 'Jeszcze nie skończyłeś? Zapisz szkic, aby dokończyć zapytanie później bez publikowania go od razu.',
      screenshot: {
        src: '/how-to/kunde/09-veroeffentlicht.jpg',
        alt: 'Opublikowane zapytanie ze statusem „Opublikowano” i terminem składania ofert',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Porównanie ofert i przygotowanie kontaktu',
      whatYouSee:
        'Lista otrzymanych ofert posortowana od najniższej ceny, z nazwą firmy, ceną i oznaczeniem ' +
        'najlepszej oferty, a także przycisk bezpłatnego udostępnienia kontaktu oraz wyjaśnienie, że ' +
        'dane trafiają do firmy dopiero po Twojej zgodzie.',
      whatToDo: 'Porównaj ceny i przy firmie, z którą chcesz nawiązać kontakt, kliknij przycisk udostępnienia kontaktu.',
      whatHappensNext:
        'Pojawia się okno potwierdzenia - od tego momentu zlecenie nie jest już pokazywane innym ' +
        'firmom, choć nie oznacza to jeszcze żadnego zobowiązania umownego.',
      successCriterion: 'Wybrana firma jest zaznaczona, widoczna jest informacja o ochronie danych.',
      troubleshooting: 'Jeśli żadna oferta Ci nie odpowiada, możesz ją odrzucić zamiast udostępniać kontakt.',
      screenshot: {
        src: '/how-to/kunde/10-angebot-kontakt-erklaerung.jpg',
        alt: 'Oferta z ceną, przyciskiem bezpłatnego udostępnienia kontaktu i wyjaśnieniem ochrony danych',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Uzupełnienie danych kontaktowych',
      whatYouSee:
        'Okno „Uzupełnij dane kontaktowe” z obowiązkowymi polami e-mail i telefon (wystarczy jedno z ' +
        'nich) oraz opcjonalnymi polami na imię, nazwisko i adres.',
      whatToDo: 'Podaj co najmniej jeden sposób kontaktu i potwierdź zapisanie i udostępnienie.',
      whatHappensNext: 'Tylko wybrana firma zobaczy dokładnie te dane, które tu wpiszesz - żadna inna firma ich nie otrzyma.',
      successCriterion: 'Okno się zamyka, a udostępnienie zostaje zakończone.',
      troubleshooting: 'Bez co najmniej e-maila lub telefonu nie da się zakończyć tego kroku.',
      screenshot: {
        src: '/how-to/kunde/11-kontaktdaten-dialog.jpg',
        alt: 'Okno uzupełniania danych kontaktowych z polami e-mail i telefon',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kontakt udostępniony pomyślnie',
      whatYouSee: 'Przy ofercie pojawia się zielona informacja, że firma może się już z Tobą kontaktować, a także opcja eksportu oferty do PDF.',
      whatToDo: 'Nic więcej nie trzeba robić - firma odezwie się w wybranym przez Ciebie przedziale czasowym.',
      whatHappensNext: 'Zlecenie nie jest już widoczne dla innych firm; w „Moich udostępnieniach kontaktu” widzisz wszystkie udostępnione kontakty.',
      successCriterion: 'Widoczna jest zielona informacja o powodzeniu.',
      screenshot: {
        src: '/how-to/kunde/12-freigegeben-erfolg.jpg',
        alt: 'Komunikat o pomyślnym udostępnieniu kontaktu wybranej firmie',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: 'Czy potrzebuję adresu e-mail, żeby korzystać z Roofora?',
      a:
        'Nie. Rejestracja odbywa się w pełni anonimowo, wystarczy nazwa użytkownika i hasło. Bez ' +
        'adresu e-mail nie możemy jednak aktywnie powiadamiać o nowych ofertach czy zapytaniach o ' +
        'kontakt - warto wtedy regularnie sprawdzać swoje konto lub opcjonalnie dodać adres e-mail.',
    },
    {
      q: 'Czy korzystanie z Roofora jako klient jest płatne?',
      a: 'Nie, utworzenie zapytania, otrzymywanie ofert i udostępnianie kontaktu jest obecnie bezpłatne dla klientów.',
    },
    {
      q: 'Czy muszę wybrać najtańszą ofertę?',
      a: 'Nie. Sam decydujesz, czy i z którą firmą się skontaktujesz - udostępnienie kontaktu nie oznacza jeszcze żadnej umowy.',
    },
    {
      q: 'Co się stanie, jeśli żadna firma nie złoży oferty?',
      a: 'Zapytanie pozostaje widoczne aż do upływu terminu składania ofert. Możesz je w każdej chwili edytować.',
    },
    {
      q: 'Ilu firmom mogę udostępnić swoje dane kontaktowe?',
      a: 'Każdą firmę zatwierdzasz osobno - limit na zapytanie zapobiega sytuacji, w której zbyt wiele firm kontaktuje się jednocześnie.',
    },
  ],
  primaryCta: { label: 'Złóż bezpłatne zapytanie', action: 'customer' },
  secondaryCta: { label: 'Zobacz najpierw szacunkowe koszty', action: 'link', href: '/koszt-instalacji-fotowoltaicznej' },
  relatedLinks: [
    { label: 'Złożyć zapytanie fotowoltaiczne', href: '/zlozyc-zapytanie-fotowoltaiczne' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
    { label: 'Oferta bez podawania numeru telefonu', href: '/oferta-fotowoltaiczna-bez-telefonu' },
    { label: 'Jak działa Roofora dla instalatorów', href: '/jak-dziala-dla-instalatorow' },
  ],
}
